import { DialogContent, Table, TableBody, TableRow, TableCell, DialogTitle } from '@mui/material';

import RowIfNotEmpty from './Row';
import DialogWrapper from './Wrapper';
import { EmbeddedFvError } from '../../../lib/interface';
import { getCurrencyView } from '../../../utils/format';
import { snakeCaseToTitleCase } from '../../../utils/string';

type Recipient = {
  name: string;
  recipient_account_id: string;
};

type Sender = {
  id: string;
  name: string;
  sender_type: string;
  external_user_id: string;
  sender_account: {
    institution_id?: string;
  };
};

type SenderAccount = {
  user_id: string;
  institution_id: string;
};

type Props = {
  open: boolean;
  handleClose: () => void;
  payment: Payment;
  customerAppId: string;
};

type Fee = {
  amount: number;
  currency: string;
  paid_by: 'SENDER' | 'RECIPIENT';
  paid_by_account_id: string;
};

type PaymentProcessorDetails = {
  processor_id?: string;
  auth_code?: string;
  processor_reference?: string;
  scheme?: string;
  result?: {
    decline_code?: string;
    decline_reason?: string;
    last_action?: string;
    result_code?: string;
    status?: string;
  };
  [key: string]: unknown;
};

type PaymentDetails = {
  description: string;
  mandate_id: string;
  external_transaction_reference: string;
  collection_entity_name?: string;
  processor_entity_name?: string;
  recurring_payment_mode?: string;
  references?: {
    finverse_transaction_reference?: string;
    bank_transaction_reference?: string;
    dda_reference?: string;
  };
  processor_details?: PaymentProcessorDetails;
  [key: string]: unknown;
};

export type Payment = {
  payment_id: string;
  amount: number;
  amount_total_with_surcharge: number;
  surcharge_amount: number;
  currency: string;
  type: string;
  updated_at: string;
  status: string;
  payment_details: PaymentDetails;
  recipient: Recipient;
  sender: Sender;
  sender_account: SenderAccount;
  created_at: string;
  payment_method_id?: string;
  payment_method?: {
    card: {
      card_details: {
        brand: string;
        last4: string;
      };
    };
  };
  recipient_account?: { settlement_account_reference?: string };
  metadata?: Record<string, string>;
  fees: Fee[];
  error?: EmbeddedFvError;
};

export function PaymentDetails({ open, handleClose, payment, customerAppId }: Props) {
  const { feeRowsSender, feeRowsRecipient } = renderFees(payment);

  return (
    <DialogWrapper
      id=""
      onClose={handleClose}
      aria-labelledby="customized-dialog-title"
      title="Payment Details"
      open={open}
    >
      <DialogContent dividers>
        <Table>
          <TableBody>
            <TableRow>
              <TableCell>Amount</TableCell>
              <TableCell align="right">{getCurrencyView(payment.currency, payment.amount)}</TableCell>
            </TableRow>
            {feeRowsSender}
            <TableRow>
              <TableCell>Total Amount with Surcharge</TableCell>
              <TableCell align="right">
                {getCurrencyView(payment.currency, payment.amount_total_with_surcharge)}
              </TableCell>
            </TableRow>
            {feeRowsRecipient}
            {RowIfNotEmpty('Customer App ID', customerAppId)}
            <TableRow>
              <TableCell>Recipient Name</TableCell>
              <TableCell align="right">{payment.recipient.name}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Recipient Account ID</TableCell>
              <TableCell align="right">{payment.recipient.recipient_account_id}</TableCell>
            </TableRow>
            {RowIfNotEmpty('Settlement Account Reference', payment.recipient_account?.settlement_account_reference)}
            {RowIfNotEmpty('Payment Method ID', payment.payment_method_id)}
            <TableRow>
              <TableCell>Description</TableCell>
              <TableCell align="right">{payment.payment_details.description}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>External Transaction Reference</TableCell>
              <TableCell align="right">{payment.payment_details.external_transaction_reference}</TableCell>
            </TableRow>
            {RowIfNotEmpty(
              'Finverse Transaction Reference',
              payment.payment_details.references?.finverse_transaction_reference,
            )}
            {renderAdditionalPaymentDetails(payment)}
          </TableBody>
        </Table>
        <MetadataSection metadata={payment.metadata} />
        {renderProcessorDetails(payment.payment_details.processor_details)}
      </DialogContent>
    </DialogWrapper>
  );
}

function renderRowsAlphabetically(rows: Record<string, string | undefined>): JSX.Element[] {
  return Object.entries(rows)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([label, value]) => RowIfNotEmpty(label, value, { key: label }));
}

function renderAdditionalPaymentDetails(payment: Payment): JSX.Element[] {
  const details = payment.payment_details;
  const rows: Record<string, string | undefined> = {
    'Bank Transaction Reference': details.references?.bank_transaction_reference,
    'Collection Entity Name': details.collection_entity_name,
    'DDA Reference': details.references?.dda_reference,
    'Mandate ID': details.mandate_id,
    'Processor Entity Name': details.processor_entity_name,
    'Recurring Payment Mode': details.recurring_payment_mode,
    'Card brand': payment.payment_method?.card.card_details.brand,
    'Card number (last 4)': payment.payment_method?.card.card_details.last4,
    'Sender Name': payment.sender.name,
    'Finverse User ID': payment.sender.id,
    'External User ID': payment.sender.external_user_id,
    'Sender Institution': payment.sender.sender_account?.institution_id,
    'Sender Type': payment.sender.sender_type,
  };

  const handledKeysInDetails = [
    'description',
    'external_transaction_reference',
    'collection_entity_name',
    'mandate_id',
    'processor_entity_name',
    'recurring_payment_mode',
    'processor_details',
    'references',
    'transaction_reference_id', // deprecated
  ];

  // Add unmapped keys from details to rows
  for (const [key, value] of Object.entries(details)) {
    if (handledKeysInDetails.includes(key) || typeof value !== 'string') continue;
    const label = snakeCaseToTitleCase(key);
    if (rows[label] === undefined) rows[label] = value;
  }

  return renderRowsAlphabetically(rows);
}

function renderProcessorDetails(details: PaymentProcessorDetails | undefined): JSX.Element | null {
  if (!details) return null;

  const rows: Record<string, string | undefined> = {
    'Auth Code': details.auth_code,
    'Processor Reference': details.processor_reference,
    'Result: Decline Reason': details.result?.decline_reason,
    'Result: Result Code': details.result?.result_code,
  };

  const handledKeysInProcessorDetails = ['processor_id', 'auth_code', 'processor_reference', 'result'];
  // Add unmapped keys from processor details to rows
  for (const [key, value] of Object.entries(details)) {
    if (handledKeysInProcessorDetails.includes(key) || typeof value !== 'string') continue;
    const label = snakeCaseToTitleCase(key);
    if (rows[label] === undefined) rows[label] = value;
  }

  return (
    <>
      <DialogTitle sx={{ textAlign: 'center' }}>Payment Processor Details</DialogTitle>
      <Table sx={{ tableLayout: 'fixed' }}>
        <TableBody>
          {RowIfNotEmpty('Processor ID', details.processor_id)}
          {renderRowsAlphabetically(rows)}
        </TableBody>
      </Table>
    </>
  );
}

function MetadataSection({ metadata }: { metadata?: Record<string, string> }) {
  if (!metadata || Object.keys(metadata).length === 0) return null;

  return (
    <>
      <DialogTitle sx={{ textAlign: 'center' }}>Metadata</DialogTitle>
      <Table sx={{ tableLayout: 'fixed' }}>
        <TableBody>{renderRowsAlphabetically(metadata)}</TableBody>
      </Table>
    </>
  );
}
const renderFees = (payment: Payment): { feeRowsRecipient: JSX.Element[]; feeRowsSender: JSX.Element[] } => {
  const feeRowsRecipient: JSX.Element[] = [];
  const feeRowsSender: JSX.Element[] = [];

  for (const fee of payment.fees) {
    if (fee.paid_by === 'SENDER') {
      feeRowsSender.push(RowIfNotEmpty('Fee Surcharge (paid by sender)', getCurrencyView(fee.currency, fee.amount)));
    } else {
      feeRowsRecipient.push(RowIfNotEmpty('Payment Fee', getCurrencyView(fee.currency, fee.amount)));
    }
  }

  return { feeRowsRecipient, feeRowsSender };
};
